import type { ReactNode } from 'react';

interface StatCardProps {
  label: string;
  value: string | number;
  icon: ReactNode;
  trend?: { value: number; label: string };
  /** Hourly (or sampled) series for the mini chart. */
  sparklineData?: number[];
  /**
   * 0–1 fill for the scalar bar when sparklineData is missing.
   * If omitted, derived from `value` (percentages and small counts handled).
   */
  meter?: number;
  /** One-line definition (title + optional sublabel). */
  hint?: string;
  color?: 'accent' | 'green' | 'red' | 'amber' | 'cyan' | 'purple' | 'teal';
}

const colorMap = {
  accent: { line: '#3b82f6', soft: '59,130,246' },
  green: { line: '#10b981', soft: '16,185,129' },
  red: { line: '#ef4444', soft: '239,68,68' },
  amber: { line: '#f59e0b', soft: '245,158,11' },
  cyan: { line: '#06b6d4', soft: '6,182,212' },
  purple: { line: '#a855f7', soft: '168,85,247' },
  teal: { line: '#14b8a6', soft: '20,184,166' },
};

function parseNumeric(value: string | number): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const m = value.replace(/,/g, '').match(/-?\d+(\.\d+)?/);
    if (m) return parseFloat(m[0]);
  }
  return null;
}

/** Compact SVG sparkline with correct scaling (incl. flat / all-zero series). */
function MiniSparkline({ data, color }: { data: number[]; color: string }) {
  const w = 120;
  const h = 40;
  const padX = 2;
  const padY = 4;

  const clean = data.map((n) => (Number.isFinite(n) ? Math.max(0, n) : 0));
  if (clean.length < 2) return null;

  const min = Math.min(...clean);
  const max = Math.max(...clean);
  const range = max - min;
  const yMin = range === 0 ? min - 1 : min;
  const yMax = range === 0 ? max + 1 : max;
  const yRange = yMax - yMin || 1;

  const pts = clean.map((v, i) => {
    const x = padX + (i / (clean.length - 1)) * (w - padX * 2);
    const y = padY + (1 - (v - yMin) / yRange) * (h - padY * 2);
    return `${x.toFixed(2)},${y.toFixed(2)}`;
  });

  const allZero = max === 0;
  // Flat non-zero: still draw a clear mid line (avoid hairline near edge)
  const stroke = allZero ? `${color}66` : color;
  const strokeW = allZero ? 1.5 : range === 0 ? 2.25 : 2;

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      className="w-full h-full"
      preserveAspectRatio="none"
      aria-hidden
    >
      <polyline
        fill="none"
        stroke={stroke}
        strokeWidth={strokeW}
        strokeLinecap="round"
        strokeLinejoin="round"
        points={pts.join(' ')}
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

/**
 * Scalar visual when we don't have hourly series:
 * track + fill bar + soft glow (always visible, works for 0).
 */
function ScalarMeter({
  ratio,
  color,
  soft,
  icon,
}: {
  ratio: number;
  color: string;
  soft: string;
  icon?: ReactNode;
}) {
  const r = Math.max(0, Math.min(1, ratio));
  const empty = r <= 0.001;

  return (
    <div className="w-full h-full flex flex-col justify-end gap-2 pb-0.5 relative">
      <div
        className="absolute inset-0 opacity-30 pointer-events-none"
        style={{
          background: `radial-gradient(ellipse 80% 60% at 50% 80%, rgba(${soft},0.35) 0%, transparent 70%)`,
        }}
      />
      {icon && (
        <div className="absolute top-0 right-0 opacity-25 text-gray-400 scale-90">
          {icon}
        </div>
      )}
      <div className="relative h-2 sm:h-2.5 rounded-full bg-white/[0.06] overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500 ease-out"
          style={{
            width: empty ? '4%' : `${Math.max(r * 100, 3)}%`,
            background: empty
              ? `rgba(${soft},0.25)`
              : `linear-gradient(90deg, rgba(${soft},0.45) 0%, ${color} 100%)`,
            boxShadow: empty ? 'none' : `0 0 12px rgba(${soft},0.45)`,
          }}
        />
      </div>
      <div className="relative flex gap-0.5 h-3 sm:h-4 items-end opacity-70">
        {Array.from({ length: 12 }).map((_, i) => {
          const t = (i + 1) / 12;
          const active = !empty && t <= r + 0.05;
          const barH = 20 + Math.round(Math.sin(i * 0.9) * 35 + (active ? r * 45 : 10));
          return (
            <div
              key={i}
              className="flex-1 rounded-sm min-w-0"
              style={{
                height: `${barH}%`,
                background: active ? color : `rgba(${soft},0.12)`,
                opacity: active ? 0.55 + r * 0.4 : 0.35,
              }}
            />
          );
        })}
      </div>
    </div>
  );
}

function deriveMeter(value: string | number, explicit?: number): number {
  if (typeof explicit === 'number' && Number.isFinite(explicit)) {
    return Math.max(0, Math.min(1, explicit));
  }
  const n = parseNumeric(value);
  if (n === null) return 0;
  // Percent strings like "71%"
  if (typeof value === 'string' && value.trim().endsWith('%')) {
    return Math.max(0, Math.min(1, n / 100));
  }
  // Small integers (callbacks, canaries): step scale so 1 still shows
  if (n <= 0) return 0;
  if (n < 20) return Math.min(1, 0.15 + (n / 20) * 0.85);
  // Larger counts: log-ish so tiles don't always look full
  return Math.min(1, Math.log10(n + 1) / 4);
}

export function StatCard({
  label,
  value,
  icon,
  trend,
  sparklineData,
  meter,
  hint,
  color = 'accent',
}: StatCardProps) {
  const colors = colorMap[color];
  const series = (sparklineData || []).filter((n) => Number.isFinite(n));
  const hasSpark = series.length >= 2;
  const ratio = deriveMeter(value, meter);
  const hintId = hint ? `stat-hint-${label.replace(/\W+/g, '-').toLowerCase()}` : undefined;

  return (
    // Outer wrapper is NOT overflow-hidden so the hint panel can escape the
    // card; the inner card keeps overflow-hidden for the gradient/sparkline.
    <div className="group relative h-full">
      {hint && (
        <div
          id={hintId}
          role="tooltip"
          className="pointer-events-none absolute bottom-full left-0 right-0 z-30 mb-2 hidden rounded-lg border border-white/10 bg-[#0d1117] p-3 text-[11px] leading-relaxed text-gray-300 shadow-xl shadow-black/60 group-hover:block group-focus-within:block"
        >
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-500">
            {label}
          </div>
          {hint}
        </div>
      )}
      <div
        className="bg-white/[0.01] border border-white/[0.025] rounded-xl p-3 sm:p-4 hover:border-white/[0.06] transition-all relative overflow-hidden min-h-[112px] sm:h-[140px] flex flex-col backdrop-blur-sm"
        tabIndex={hint ? 0 : undefined}
        aria-describedby={hintId}
      >
      <div className="absolute inset-0 bg-gradient-to-br from-white/[0.015] to-transparent pointer-events-none" />

      <div className="relative flex flex-col h-full min-w-0">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="min-w-0">
            <span className="text-[10px] sm:text-xs text-gray-400 uppercase tracking-wider font-medium leading-tight line-clamp-2 block">
              {label}
            </span>
          </div>
          <div className="text-lg sm:text-2xl font-semibold text-gray-300 tracking-tight shrink-0 tabular-nums">
            {value}
          </div>
        </div>

        <div className="flex-1 relative min-h-[40px] sm:min-h-[52px]">
          {hasSpark ? (
            <div className="w-full h-full opacity-90">
              <MiniSparkline data={series} color={colors.line} />
            </div>
          ) : (
            <ScalarMeter
              ratio={ratio}
              color={colors.line}
              soft={colors.soft}
              icon={icon}
            />
          )}
        </div>

        {trend && (
          <div className="mt-auto flex items-center gap-1.5 pt-1">
            <span
              className={`text-xs font-medium ${
                trend.value >= 0 ? 'text-green-400' : 'text-red-400'
              }`}
            >
              {trend.value >= 0 ? '↑' : '↓'} {Math.abs(trend.value)}%
            </span>
            <span className="text-xs text-gray-500">{trend.label}</span>
          </div>
        )}
      </div>
      </div>
    </div>
  );
}
